import { useEffect, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { AdminUserRef } from '../api/useHypoteseBehandling.ts';
import { useEnSide, type OnePage } from '../api/useHypoteseLenker.ts';
import { useHypoteseConfig, type HypothesisConfig } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { erApiFeil } from '../api/feil.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { hypoteseOppforing, hypoteseStatusNoekkel } from '../status/status.ts';
import { Tekstknapp, Varsel } from './deler.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { adresseUtenSkjema } from './Lenker.tsx';
import { statusNavn } from './lesning.ts';
import { delsetning, kortDato, kortDatoAv, mellomromFoer, retningOrd, tall } from './setning.ts';
import type { Skjema } from './skjema.ts';

/**
 * H3.5 del B — «Én side» (H23; konseptsiden skjerm 9–13, P8, P9): utskriften til ledergruppen for
 * en konkludert eller stoppet hypotese, i ny fane fra detaljen.
 *
 * Siden henter innholdet med ETT kall (`POST /one-page`), og det kallet fører eksporten i loggen.
 * Feiler det, er det ingenting å skrive ut — «Skriv ut» finnes ikke, og ingenting er ført. Siden
 * har ingen meny og bare én handling. Toppen og rammen rundt følger panelets palett; arket er lyst
 * uansett (`data-innspel-theme="light"`), og det som kommer på papiret, er det som står på arket.
 * Aldri notater, aldri poeng — de hentes ikke en gang. Papiret er tegnet uten bakgrunner: status er
 * en omrisset pille med ikon og ord (DD-08 holder i gråtoner), brikkene i setningen er halvfet tekst.
 *
 * Utskriften er nettleserens egen, og «Lagre som PDF» er nettleserens valg — Innspel lager ingen fil.
 * Alt er stilark fra samme opprinnelse; CSP-en (`default-src 'none'`, K8) trenger ingenting nytt.
 */
export function EnSideRute() {
  const { nr } = useParams();
  const n = Number((nr ?? '').replace(/^h-/i, ''));
  return <EnSide nr={Number.isInteger(n) && n > 0 ? n : null} />;
}

const AAR = (iso: string) => new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', year: 'numeric' }).format(new Date(iso));
const MAANEDER_LANG = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];
/** «7. desember 2026», i Europe/Oslo. */
function helDato(iso: string): string {
  const deler = new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date(iso));
  const hent = (t: string) => Number(deler.find((p) => p.type === t)?.value ?? '0');
  return `${hent('day')}. ${MAANEDER_LANG[hent('month') - 1] ?? ''} ${hent('year')}`;
}

const navn = (r: AdminUserRef | undefined) => r?.displayName?.trim() || undefined;

function EnSide({ nr }: { nr: number | null }) {
  const smal = useSmal();
  const naviger = useNavigate();
  const side = useEnSide(nr);
  const config = useHypoteseConfig();
  const me = useMe();
  const tittel = `${nummerTekst(nr ?? undefined)} · Én side`;

  useEffect(() => {
    const forrige = document.title;
    document.title = `${tittel} · Innspel`;
    return () => {
      document.title = forrige;
    };
  }, [tittel]);

  // «Lukk»: fanen ble åpnet fra detaljen og lukkes; åpnet direkte går den tilbake til hypotesen.
  const lukk = () => {
    if (window.history.length <= 1) window.close();
    void naviger(nr ? `/h/registeret/${nr}` : '/h');
  };
  const skrivUt = () => window.print();

  const data = side.data;
  const klar = data && config.data;
  const laster = side.isPending || (data && config.isPending);

  let innhold: ReactNode;
  if (laster) {
    innhold = <Skjelett etikett="Lager siden …" />;
  } else if (side.isError || data === null || data === undefined) {
    const e = side.error;
    const tekst =
      data === null || (erApiFeil(e) && e.status === 404)
        ? `Fant ikke ${nummerTekst(nr ?? undefined)}. Den kan være slettet, eller du har ikke tilgang til den.`
        : erApiFeil(e) && e.code === 'invalid_status_transition'
          ? `${nummerTekst(nr ?? undefined)} er ikke konkludert lenger. Siden kan lages når konklusjonen er bekreftet.`
          : 'Siden kunne ikke lages. Ingenting er skrevet ut.';
    innhold = (
      <div className="es-feil">
        <Varsel tone="info" handling={<Tekstknapp onClick={() => void side.refetch()}>Prøv igjen</Tekstknapp>}>
          {tekst}
        </Varsel>
        <Skjelett />
      </div>
    );
  } else if (klar) {
    innhold = <Ark s={data} config={config.data} leietaker={me.data?.tenantName} />;
  }

  const setning = <span className="es-hjelp">Notater og poeng er aldri med.</span>;
  const knapp = (
    <button type="button" className="panel-knapp es-skriv-ut" aria-keyshortcuts="Control+P" onClick={skrivUt}>
      <Ikon navn="print-outline" />
      Skriv ut
      {smal ? null : <kbd>Ctrl P</kbd>}
    </button>
  );

  return (
    <div className={smal ? 'es es-smal' : 'es'} data-testid="es">
      <header className="es-topp">
        <button type="button" className="panel-knapp-lenke es-lukk" onClick={lukk}>
          <Ikon navn="chevron-back-outline" />
          Lukk
        </button>
        <span className="es-topp-tittel">{tittel}</span>
        <span className="es-topp-h">
          {klar && !smal ? (
            <>
              {setning}
              {knapp}
            </>
          ) : null}
        </span>
      </header>
      <main className="es-bord">{innhold}</main>
      {klar && smal ? (
        <footer className="es-fot">
          {knapp}
          {setning}
        </footer>
      ) : null}
    </div>
  );
}

function Skjelett({ etikett }: { etikett?: string }) {
  return (
    <div className="es-skjelett" {...(etikett ? { role: 'status', 'aria-label': etikett } : { 'aria-hidden': true })}>
      <span className="panel-skjelett-linje panel-skjelett-tittel" />
      <span className="panel-skjelett-linje" />
      <span className="panel-skjelett-linje" />
      <span className="panel-skjelett-linje" />
      <span className="panel-skjelett-linje" />
    </div>
  );
}

/** Formens brikker til setningen — de samme ordene som tjenerens `sentence`. */
function skjemaAv(s: OnePage): Skjema {
  return {
    title: s.title,
    change: s.change,
    audience: s.audience,
    effect: s.effect,
    metricName: s.metricName,
    sourceText: s.sourceText,
    baselineState: s.baselineState,
    baselineValue: s.baselineValue != null ? String(s.baselineValue) : '',
    baselineOn: s.baselineOn,
    direction: s.direction,
    threshold: String(s.threshold),
    unit: s.unit,
    testEndsOn: s.testEndsOn,
    whatWeLose: '',
    background: '',
    differenceNote: '',
  };
}

/**
 * Arket (skjerm 10, 11). P17s rekkefølge: tittel og status, setningen, i dag → terskel → resultat, så
 * radene, hvem og datoene, og «Skrevet ut … fra Innspel» nederst. Topplinjen er tabellens hode, så
 * nettleseren gjentar den på side 2; en rad brytes aldri inne i seg selv.
 */
function Ark({ s, config, leietaker }: { s: OnePage; config: HypothesisConfig; leietaker: string | undefined }) {
  const k = s.conclusion;
  const stopp = s.status === 'stoppet';
  const noekkel = hypoteseStatusNoekkel(s.status, s.outcome);
  const oppf = hypoteseOppforing(noekkel);
  const stoppkode = k.stopReason ? config.stopReasonWording.find((w) => w.code === k.stopReason)?.label : undefined;
  const enhet = (v: number) => `${tall(v)} ${s.unit}`.trim();
  const nr = nummerTekst(s.number);

  const statuslinje = stopp
    ? [stoppkode, `stoppet${navn(k.writtenBy) ? ` av ${navn(k.writtenBy)}` : ''} ${helDato(k.frozenAt)}`].filter(Boolean).join(' · ')
    : `bekreftet${navn(k.confirmedBy) ? ` av ${navn(k.confirmedBy)}` : ''} ${helDato(k.confirmedAt ?? k.frozenAt)}`;

  const rader: Array<[string, ReactNode]> = [
    [stopp ? 'Hva vi rakk å lære' : 'Hva vi lærte', k.learned],
    ['Åpne blokkerende', k.openBlocking],
    ['Begrensninger i data', k.dataLimitations],
    ['Uenighet', k.dissent?.trim() ? k.dissent : <span className="es-dempet">Ingen uenighet ført.</span>],
  ];
  if (s.notDoing?.trim()) rader.push(['Det vi ikke gjorde', s.notDoing]);
  if (k.nextSteps?.trim()) rader.push(['Neste steg', k.nextSteps]);
  if (s.evidenceCount > 0) rader.push(['Grunnlag', `${s.evidenceCount} ${s.evidenceCount === 1 ? 'sak' : 'saker'} fra Innspill`]);
  if (s.externalLinks.length > 0) {
    rader.push([
      'Lenker',
      <ul className="es-lenker">
        {s.externalLinks.map((l) => (
          <li key={l.id}>
            {l.label}
            <span className="es-adresse">{adresseUtenSkjema(l.url)}</span>
          </li>
        ))}
      </ul>,
    ]);
  }
  const hvem = [
    navn(s.submittedBy) ? `Foreslått av ${navn(s.submittedBy)}` : 'Foreslått uten navn',
    s.reviewer ? `vurdert av ${navn(s.reviewer) ?? 'en uten navn'}` : null,
    s.owner ? `eier ${navn(s.owner) ?? 'uten navn'}` : null,
    s.decider ? `beslutningstaker ${navn(s.decider) ?? 'uten navn'}` : null,
  ].filter(Boolean);
  rader.push(['Hvem', hvem.join(' · ')]);
  const datoer = [
    `Sendt inn ${kortDatoAv(s.submittedAt)}`,
    s.selectedAt ? `valgt ${kortDatoAv(s.selectedAt)}` : null,
    s.startedAt ? `startet ${kortDatoAv(s.startedAt)}` : null,
    stopp ? null : `konkludert ${kortDatoAv(k.writtenAt)}`,
    stopp ? `stoppet ${kortDatoAv(k.frozenAt)} ${AAR(k.frozenAt)}` : `bekreftet ${kortDatoAv(k.confirmedAt ?? k.frozenAt)} ${AAR(k.confirmedAt ?? k.frozenAt)}`,
  ].filter(Boolean);
  rader.push(['Datoer', datoer.join(' · ')]);

  const deler = delsetning(skjemaAv(s));

  return (
    <article className="es-ark" data-innspel-theme="light" aria-labelledby="es-tittel" data-testid="es-ark">
      <table className="es-ramme" role="presentation">
        <thead>
          <tr>
            <td>
              <div className="es-pp">
                <span>{leietaker ? `${leietaker} · Hypoteser` : 'Hypoteser'}</span>
                <span>{nr}</span>
              </div>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className="es-innhold">
                <div className="es-hode">
                  <h1 id="es-tittel" className="es-tittel">
                    {s.title}
                  </h1>
                  <p className="es-status-linje" data-testid="es-status-linje">
                    <span className="es-pille" data-testid="es-status">
                      <Ikon navn={oppf.ikon} />
                      {statusNavn(config, { status: s.status, outcome: s.outcome })}
                    </span>
                    <span className="es-dempet">{statuslinje}</span>
                  </p>
                </div>
                <p className="es-setning">
                  {deler.map((d, i) => (
                    <span key={i}>
                      {mellomromFoer(d, i) ? ' ' : ''}
                      {d.type === 'fw' ? d.tekst : <b>{d.tekst}</b>}
                    </span>
                  ))}
                </p>
                <dl className="es-rekke">
                  <div>
                    <dt>{`I dag · ${kortDato(s.baselineOn)}`}</dt>
                    <dd>{s.baselineValue != null ? enhet(s.baselineValue) : 'ikke målt'}</dd>
                  </div>
                  <div>
                    <dt>Terskel</dt>
                    <dd>{`${retningOrd(s.direction)} ${enhet(s.threshold)}`}</dd>
                  </div>
                  <div className="es-rekke-res" data-testid="es-resultat">
                    <dt>{k.resultMeasuredOn ? `Resultat · ${kortDato(k.resultMeasuredOn)}` : 'Resultat'}</dt>
                    <dd>{k.resultValue != null ? enhet(k.resultValue) : 'ikke målt'}</dd>
                  </div>
                </dl>
                <dl className="es-rader">
                  {rader.map(([etikett, verdi]) => (
                    <div key={etikett} className="es-rad" data-testid="es-rad">
                      <dt>{etikett}</dt>
                      <dd>{verdi}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>
              <div className="es-arkfot">{`Skrevet ut ${helDato(s.exportedAt)} fra Innspel`}</div>
            </td>
          </tr>
        </tfoot>
      </table>
    </article>
  );
}
